<template>
  <AppContainer :is-loading>
    <div>
      <EPageTitleMobile
        :title="t(useTranslationPath(callerPath, 'title'))"
        :description="t(useTranslationPath(callerPath, 'description'))"
      />

      <EForm>
        <div class="flex justify-center text-center">
          <EFormField
            id="confirmation_code"
            :label="t(useTranslationPath(callerPath, 'label'))"
            :errors="getFieldErrors(errors, 'confirmation_code')"
          >
            <EInputCode
              id="confirmation_code"
              v-model="v$.confirmationCode.$model"
              name="confirmation_code"
            />
          </EFormField>
        </div>
      </EForm>

      <div class="justify-center space-y-2 text-center">
        <div class="mt-8 text-xs">
          {{ t(useTranslationPath(callerPath, 'resendPrompt')) }}
        </div>
        <EButton
          size="sm"
          :label="t(useTranslationPath(callerPath, 'resend'))"
          variant="secondary"
          @click="onResend"
        />
      </div>
    </div>

    <template #bottom>
      <EButton
        :label="t(useTranslationPath(callerPath, 'confirm'))"
        block
        :disabled="v$.$invalid"
        @click="onSubmit"
      />
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore.js'
import { EButton, EForm, EFormField, EInputCode, EPageTitleMobile } from '@expansivefm/eds'
import useVuelidate from '@vuelidate/core'
import { minLength, numeric, required } from '@vuelidate/validators'
import { inject, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()
const $apiUnauthenticatedRequest = inject('$apiUnauthenticatedRequest')
const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const callerPath = 'views.unauthenticated.verification.methods.confirm'
const { handleErrors, getFieldErrors } = useApiErrorHandler(router)

const isLoading = ref(null)
const errors = ref([])

onMounted(async () => {
  appStore.showBackButton = false
  isLoading.value = true

  isLoading.value = false
  appStore.showBackButton = true
})

const form = reactive({
  confirmationCode: null,
})

const validationRules = {
  confirmationCode: {
    required,
    numeric,
    minLength: minLength(6),
  },
}

const v$ = useVuelidate(validationRules, form)

const onResend = async () => {
  isLoading.value = true

  const userPendingVerificationMethod = route.params.userPendingVerificationMethod

  try {
    await $apiUnauthenticatedRequest.patch(
      'user-pending-verification-methods',
      $apiUnauthenticatedRequest.configs.patch.setId(userPendingVerificationMethod).setData({
        status: 'PENDING',
      }),
    )
  } catch (error) {
    await handleErrors(error)
  } finally {
    isLoading.value = false
  }
}

const onSubmit = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  isLoading.value = true

  const userPendingVerificationMethod = route.params.userPendingVerificationMethod

  try {
    await $apiUnauthenticatedRequest.patch(
      'user-pending-verification-methods',
      $apiUnauthenticatedRequest.configs.patch.setId(userPendingVerificationMethod).setData({
        status: 'CONFIRMED',
        confirmation_code: form.confirmationCode,
      }),
    )

    await router.push({
      name: 'auth.verification.methods.confirmed',
    })
  } catch (error) {
    const result = await handleErrors(error)
    errors.value = result.validationErrors
  } finally {
    isLoading.value = false
  }
}
</script>
